import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ICP_RATINGS_SAVE_DELAY_MS,
  type AnswerIcpQuestionRequest,
  type IcpAnswer,
  type IcpInterviewView,
  type IcpProfileView,
  type IcpRating,
  type IcpSaveResult,
  type IcpView,
  type SaveIcpRequest,
  type StartIcpInterviewRequest,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPatch, apiPost, apiPut, errorMessage } from '../../../lib/api-client';
import { useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { itemOfInput, type RatingOverlay } from '../icp-form';

// ADR 0061 — the ideal candidate profile on RNK-01. The profile and its asks live under the Jev key the server's progress
// hints name, so a running ask is followed with no polling (#12). Every write is applied on screen first and reconciled
// after (CLAUDE.md §5a): a rating re-orders the table at once, and the clicks stay laid over the server's ratings until the
// refetch that follows has landed.

/**
 * The profile hangs under the same key the Jev progress hints invalidate (`jevKey` in ranking's jev-api), written out here so this
 * feature does not import Ranking back; icp-api.test.tsx proves the two start the same.
 */
export function icpKey(jobId: string): readonly unknown[] {
  return ['applications', 'jev', jobId, 'icp'];
}

/** The interview is the asker's own and changes only by their own actions, so the server sends no hints for it. */
export function interviewKey(jobId: string): readonly unknown[] {
  return ['applications', 'icp-interview', jobId];
}

function icpPath(jobId: string): string {
  return `/jobs/${jobId}/ranking/icp`;
}

const PENDING_PROFILE_ID = 'pending-profile';

/** The profile and the newest ask on it. The last answer stays on screen while a refetch runs (CLAUDE.md §5a rule 2). */
export function useIcp(jobId: string, isOn: boolean): UseQueryResult<IcpView, ApiError> {
  return useQuery<IcpView, ApiError>({
    queryKey: icpKey(jobId),
    queryFn: () => apiGet<IcpView>(icpPath(jobId)),
    enabled: isOn,
    placeholderData: (previous) => previous,
  });
}

// ---- Saving the profile (F23.22) ----

const STALE_PROFILE_TOAST = 'Someone else changed this profile. Your changes are still here; check them and save again.';

/** The profile as the card will show it once the server agrees: fact titles written, the form's ratings both the focus and the baseline. */
export function optimisticView(before: IcpView, input: SaveIcpRequest, updatedByName: string | null): IcpView {
  const items = input.items.map(itemOfInput);
  const profile: IcpProfileView = {
    id: before.profile?.id ?? PENDING_PROFILE_ID,
    items,
    baselineRatings: Object.fromEntries(items.map((item) => [item.id, item.rating])),
    version: before.profile?.version ?? 1,
    updatedAt: new Date().toISOString(),
    updatedByName,
  };
  return { ...before, profile };
}

/**
 * F23.22: "Save and rank with this". The card shows the new profile before the server answers; a refusal puts the old one
 * back, says why and hands the form its words again (`onFailed`), so nothing typed is lost. A 409 is someone else's save.
 */
export function useSaveIcp(jobId: string, handlers: { readonly onSaved: (result: IcpSaveResult) => void; readonly onFailed: (input: SaveIcpRequest) => void }): { readonly save: (input: SaveIcpRequest) => Promise<void> } {
  const queryClient = useQueryClient();
  const userName = useCurrentSession()?.fullName ?? null;
  const show = useToasts((toasts) => toasts.show);
  const key = icpKey(jobId);
  const { onSaved, onFailed } = handlers;

  const save = async (input: SaveIcpRequest): Promise<void> => {
    await queryClient.cancelQueries({ queryKey: key });
    const before = queryClient.getQueryData<IcpView>(key);
    if (before) queryClient.setQueryData<IcpView>(key, optimisticView(before, input, userName));
    try {
      const result = await apiPut<IcpSaveResult>(icpPath(jobId), input);
      queryClient.setQueryData<IcpView>(key, result.view);
      await queryClient.invalidateQueries({ queryKey: key });
      if (result.rankProblem) show({ tone: 'warning', message: 'The profile is saved, but Jev didn’t start', detail: result.rankProblem });
      onSaved(result);
    } catch (error) {
      queryClient.setQueryData<IcpView>(key, before);
      show({ tone: 'danger', message: 'The profile wasn’t saved', detail: error instanceof ApiError && error.status === 409 ? STALE_PROFILE_TOAST : errorMessage(error) });
      onFailed(input);
    }
  };

  return { save };
}

/** "Rank again": read every CV against the profile once more, for example after new applicants arrived. */
export function useRankIcp(jobId: string): { readonly rank: () => Promise<void>; readonly isRanking: boolean } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [isRanking, setRanking] = useState(false);
  const rank = async (): Promise<void> => {
    setRanking(true);
    try {
      queryClient.setQueryData<IcpView>(icpKey(jobId), await apiPost<IcpView>(`${icpPath(jobId)}/rank`, {}));
      await queryClient.invalidateQueries({ queryKey: icpKey(jobId) });
    } catch (error) {
      show({ tone: 'danger', message: 'Jev didn’t run', detail: errorMessage(error) });
    } finally {
      setRanking(false);
    }
  };
  return { rank, isRanking };
}

// ---- Power sorting (F23.23) ----

export interface IcpRatings {
  /** The clicks the server has not confirmed yet, laid over its ratings by `withRatings`. */
  readonly overlay: RatingOverlay;
  readonly setRating: (itemId: string, rating: IcpRating) => void;
  /** Back to the ratings as last saved in the form. `baseline` is what the overlay shows at once. */
  readonly reset: (baseline: RatingOverlay) => void;
  /** The form has saved new ratings: clicks laid over the old ones must not hide them. */
  readonly discard: () => void;
}

/** Drops the entries of `sent` that the overlay still holds as sent; a click made since stays. */
function withoutSent(overlay: RatingOverlay, sent: RatingOverlay): RatingOverlay {
  const kept: Record<string, IcpRating> = {};
  for (const [id, rating] of Object.entries(overlay)) {
    if (sent[id] !== rating) kept[id] = rating;
  }
  return kept;
}

async function send(queryClient: QueryClient, jobId: string, body: { readonly ratings: RatingOverlay } | { readonly reset: true }): Promise<void> {
  // A refetch that started before this change would put the old ratings back for a moment.
  await queryClient.cancelQueries({ queryKey: icpKey(jobId) });
  queryClient.setQueryData<IcpView>(icpKey(jobId), await apiPatch<IcpView>(`${icpPath(jobId)}/ratings`, body));
  // The overlay is let go only when this has landed, so a row never jumps back and forward again (§5a rule 3).
  await queryClient.invalidateQueries({ queryKey: icpKey(jobId) });
}

/**
 * Power sorting: a click on a dot changes the order at once, in the browser; a burst of clicks becomes one save, a
 * moment after the last (ICP_RATINGS_SAVE_DELAY_MS). It never starts a Jev search. A failure puts the ratings back.
 */
export function useIcpRatings(jobId: string): IcpRatings {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [overlay, setOverlay] = useState<RatingOverlay>({});
  const overlayRef = useRef<RatingOverlay>({});
  const unsent = useRef<Record<string, IcpRating>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const apply = useCallback((next: RatingOverlay): void => {
    overlayRef.current = next;
    setOverlay(next);
  }, []);

  const settle = useCallback(
    async (sent: RatingOverlay, body: { readonly ratings: RatingOverlay } | { readonly reset: true }): Promise<void> => {
      try {
        await send(queryClient, jobId, body);
      } catch (error) {
        show({ tone: 'danger', message: 'The focus wasn’t saved', detail: errorMessage(error) });
      }
      // Confirmed or refused, what was sent no longer needs holding: the server's ratings are on screen again.
      apply(withoutSent(overlayRef.current, sent));
    },
    [apply, jobId, queryClient, show],
  );

  const flush = useCallback((): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    const batch = unsent.current;
    unsent.current = {};
    if (Object.keys(batch).length === 0) return;
    void settle(batch, { ratings: batch });
  }, [settle]);

  const setRating = useCallback(
    (itemId: string, rating: IcpRating): void => {
      unsent.current = { ...unsent.current, [itemId]: rating };
      apply({ ...overlayRef.current, [itemId]: rating });
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(flush, ICP_RATINGS_SAVE_DELAY_MS);
    },
    [apply, flush],
  );

  const reset = useCallback(
    (baseline: RatingOverlay): void => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
      unsent.current = {};
      apply(baseline);
      void settle(baseline, { reset: true });
    },
    [apply, settle],
  );

  const discard = useCallback((): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    unsent.current = {};
    apply({});
  }, [apply]);

  // Leaving the page inside the delay still saves what was clicked.
  useEffect(() => flush, [flush]);

  return { overlay, setRating, reset, discard };
}

// ---- The interview with the profile assistant (F23.21) ----

/** The asker's own latest interview, or null. Fetched only while the panel is open. */
export function useLatestInterview(jobId: string, isOn: boolean): UseQueryResult<IcpInterviewView | null, ApiError> {
  return useQuery<IcpInterviewView | null, ApiError>({
    queryKey: interviewKey(jobId),
    queryFn: () => apiGet<IcpInterviewView | null>(`${icpPath(jobId)}/interviews/latest`),
    enabled: isOn,
    placeholderData: (previous) => previous,
  });
}

/**
 * ADR 0075: drafts the rated lines from the hire request's ideal-candidate text in one call. The drafted interview lands in the
 * interview's cache like any other, so nothing else needs reading again.
 */
export function useDraftFromRequest(jobId: string): { readonly draftFromRequest: () => Promise<IcpInterviewView | null>; readonly isDrafting: boolean } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [isDrafting, setDrafting] = useState(false);
  const draftFromRequest = async (): Promise<IcpInterviewView | null> => {
    setDrafting(true);
    try {
      const view = await apiPost<IcpInterviewView>(`${icpPath(jobId)}/interviews/from-request`, {});
      queryClient.setQueryData<IcpInterviewView | null>(interviewKey(jobId), view);
      return view;
    } catch (error) {
      show({ tone: 'danger', message: 'The lines weren’t drafted', detail: errorMessage(error) });
      return null;
    } finally {
      setDrafting(false);
    }
  };
  return { draftFromRequest, isDrafting };
}

export type InterviewStep = 'start' | 'answer' | 'next' | 'draft';

export interface InterviewActions {
  /** What the assistant is working on now: "thinking…" shows on the panel while it is set. */
  readonly step: InterviewStep | null;
  readonly start: (input: StartIcpInterviewRequest) => Promise<IcpInterviewView | null>;
  readonly answer: (interview: IcpInterviewView, input: AnswerIcpQuestionRequest) => Promise<void>;
  readonly next: (interviewId: string) => Promise<void>;
  readonly draft: (interviewId: string) => Promise<IcpInterviewView | null>;
}

function answerOf(input: AnswerIcpQuestionRequest): IcpAnswer {
  return { choices: input.choices ?? [], text: input.text ?? null, isSkipped: input.isSkipped ?? false };
}

/** The interview with the answer already in it, so the person's bubble is there before the assistant replies. */
export function withAnswer(interview: IcpInterviewView, turn: number, answer: IcpAnswer): IcpInterviewView {
  return { ...interview, problem: null, turns: interview.turns.map((entry, index) => (index === turn ? { ...entry, answer, answeredAt: new Date().toISOString() } : entry)) };
}

/**
 * The interview's five calls (PLAN §7.7). The answer shows at once and stays, even when the assistant fails to ask the
 * next question — that comes back as `problem`, with "Try again". A refusal puts the screen back and says why.
 */
export function useInterviewActions(jobId: string): InterviewActions {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [step, setStep] = useState<InterviewStep | null>(null);
  const key = interviewKey(jobId);
  const base = `${icpPath(jobId)}/interviews`;

  const put = (view: IcpInterviewView): void => {
    queryClient.setQueryData<IcpInterviewView | null>(key, view);
  };

  async function run<TResult>(which: InterviewStep, title: string, call: () => Promise<TResult>, onError?: () => void): Promise<TResult | null> {
    setStep(which);
    try {
      return await call();
    } catch (error) {
      onError?.();
      show({ tone: 'danger', message: title, detail: errorMessage(error) });
      return null;
    } finally {
      setStep(null);
    }
  }

  const start = (input: StartIcpInterviewRequest): Promise<IcpInterviewView | null> =>
    run('start', 'The interview didn’t start', async () => {
      const view = await apiPost<IcpInterviewView>(base, input);
      put(view);
      return view;
    });

  const answer = async (interview: IcpInterviewView, input: AnswerIcpQuestionRequest): Promise<void> => {
    await queryClient.cancelQueries({ queryKey: key });
    const before = queryClient.getQueryData<IcpInterviewView | null>(key) ?? interview;
    put(withAnswer(before, input.turn, answerOf(input)));
    await run(
      'answer',
      'That answer wasn’t sent',
      async () => put(await apiPost<IcpInterviewView>(`${base}/${interview.id}/answers`, input)),
      () => {
        put(before);
        void queryClient.invalidateQueries({ queryKey: key });
      },
    );
  };

  const next = async (interviewId: string): Promise<void> => {
    await run('next', 'The assistant didn’t answer', async () => put(await apiPost<IcpInterviewView>(`${base}/${interviewId}/next`, {})));
  };

  const draft = (interviewId: string): Promise<IcpInterviewView | null> =>
    run('draft', 'The profile wasn’t drafted', async () => {
      const view = await apiPost<IcpInterviewView>(`${base}/${interviewId}/draft`, {});
      put(view);
      return view;
    });

  return { step, start, answer, next, draft };
}
